import { useEffect, useRef } from "react";

/**
 * The Living Frequency Field — homepage-only canvas.
 *
 * Layered motion, each with meaning:
 *   Breath cycle  → presence
 *   Heart pulse   → the living source
 *   Ripples       → influence
 *   Cursor pull   → curiosity
 *   Surge         → devotion (primary CTA)
 *   Signature     → activation (hero symbol, once per visit)
 *   Coherence     → the field organizes itself as the visitor reads deeper
 *   Idle calm     → stillness
 *
 * Events consumed (all `window` CustomEvents):
 *   kaf:surge              { x, y }  primary CTA surge
 *   kaf:ankh-signature     { x, y }  hero symbol activation
 *   kaf:attention          { x, y }  local brightening after dwell
 *
 * Adaptive density, prefers-reduced-motion, visibility pause, resize reseed.
 */
export function LivingFrequencyField() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    const w0 = window.innerWidth;
    const density = w0 < 640 ? 0.045 : w0 < 1100 ? 0.06 : 0.08;
    const area = window.innerWidth * window.innerHeight;
    const target = Math.min(isCoarse ? 90 : 220, Math.floor((area / 10000) * density * 10));

    type P = {
      x: number; y: number;
      vx: number; vy: number;
      hx: number; hy: number;
      r: number;
      depth: number;
      hue: number;
      phase: number;
      twinkle: number;
    };

    const particles: P[] = [];
    const rand = (a: number, b: number) => a + Math.random() * (b - a);
    const seed = () => {
      particles.length = 0;
      for (let i = 0; i < target; i++) {
        const depth = Math.random();
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * window.innerHeight;
        particles.push({
          x, y, hx: x, hy: y,
          vx: rand(-0.05, 0.05), vy: rand(-0.05, 0.05),
          r: rand(0.5, 1.6) + depth * 1.4,
          depth,
          hue: Math.random(),
          phase: Math.random() * Math.PI * 2,
          twinkle: Math.random() * Math.PI * 2,
        });
      }
    };

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    seed();

    // Pointer.
    const pointer = { x: -9999, y: -9999, active: false, lastMove: 0 };
    let lastActivity = performance.now();
    const bumpActivity = () => { lastActivity = performance.now(); };
    const onMove = (e: PointerEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.active = true;
      pointer.lastMove = performance.now();
      bumpActivity();
    };
    const onLeave = () => { pointer.active = false; };

    // Ripples.
    type R = { x: number; y: number; t: number; life: number; strength: number; color?: [number, number, number] };
    const ripples: R[] = [];
    const spawnRipple = (
      x: number, y: number, strength = 1, life = 2600, color?: [number, number, number],
    ) => {
      if (ripples.length > 12) ripples.shift();
      ripples.push({ x, y, t: performance.now(), life, strength, color });
    };
    const onClick = (e: PointerEvent) => { spawnRipple(e.clientX, e.clientY, 1.2); bumpActivity(); };

    // Scroll flow + coherence (0 → 1 as visitor reads deeper).
    let scrollShift = 0;
    let scrollTarget = 0;
    let coherence = 0;
    const onScroll = () => {
      scrollTarget = window.scrollY;
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      // Coherence caps out around 60% depth — no need to reach the footer.
      coherence = Math.min(1, window.scrollY / (max * 0.6));
      bumpActivity();
    };
    onScroll();

    // Surge envelope.
    let surge = 0;
    let surgeStart = 0;
    const SURGE_RISE = 700;
    const SURGE_HOLD = 350;
    const SURGE_FALL = 2400;
    const onSurge = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      surgeStart = performance.now();
      surge = 0.0001;
      const cx = ce.detail?.x ?? window.innerWidth / 2;
      const cy = ce.detail?.y ?? window.innerHeight / 2;
      spawnRipple(cx, cy, 2.2);
      setTimeout(() => spawnRipple(cx, cy, 1.4), 220);
      setTimeout(() => spawnRipple(cx, cy, 0.9), 520);
      bumpActivity();
    };

    // Signature envelope + sacred mandala overlay.
    let signature = 0;
    let signatureStart = 0;
    let signatureX = 0;
    let signatureY = 0;
    const SIG_LIFE = 5200;
    const onSignature = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      signatureStart = performance.now();
      signature = 0.0001;
      signatureX = ce.detail?.x ?? window.innerWidth / 2;
      signatureY = ce.detail?.y ?? window.innerHeight * 0.35;
      // A cascade of golden waves that sweep the whole field.
      spawnRipple(signatureX, signatureY, 3.2, 3600, [255, 232, 168]);
      setTimeout(() => spawnRipple(signatureX, signatureY, 2.1, 3400, [244, 214, 117]), 320);
      setTimeout(() => spawnRipple(signatureX, signatureY, 1.4, 3000, [214, 168, 62]), 680);
      bumpActivity();
    };

    // Local attention brightening.
    const attentions: { x: number; y: number; t: number; life: number }[] = [];
    const onAttention = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      const x = ce.detail?.x ?? window.innerWidth / 2;
      const y = ce.detail?.y ?? window.innerHeight / 2;
      attentions.push({ x, y, t: performance.now(), life: 3200 });
      if (attentions.length > 6) attentions.shift();
      spawnRipple(x, y, 0.5, 2400);
    };

    // Breath envelope — the "One Conscious Breath" ritual. Calms the field for
    // ~4s, brightens the heart, spawns one wide golden wave.
    let breathE = 0;
    let breathEStart = 0;
    const BREATH_E_LIFE = 4200;
    const onBreath = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      breathEStart = performance.now();
      breathE = 0.0001;
      const cx = ce.detail?.x ?? window.innerWidth / 2;
      const cy = ce.detail?.y ?? window.innerHeight * 0.42;
      spawnRipple(cx, cy, 1.8, 4200, [255, 240, 190]);
      setTimeout(() => spawnRipple(heart.x, heart.y, 1.1, 3600, [255, 236, 176]), 900);
      heart.lastPulse = performance.now();
      bumpActivity();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onClick, { passive: true });
    window.addEventListener("pointerleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", () => { resize(); seed(); });
    window.addEventListener("kaf:surge", onSurge);
    window.addEventListener("kaf:ankh-signature", onSignature);
    window.addEventListener("kaf:attention", onAttention);
    window.addEventListener("kaf:breath", onBreath);

    // Pause-ripple detection.
    let pauseTimer: number | null = null;
    const armPauseRipple = () => {
      if (pauseTimer) window.clearTimeout(pauseTimer);
      pauseTimer = window.setTimeout(() => {
        if (pointer.active) spawnRipple(pointer.x, pointer.y, 0.7);
      }, 900);
    };
    window.addEventListener("pointermove", armPauseRipple, { passive: true });

    // Heart of the Field — pulses every ~8s, sends a gentle wave.
    const heart = { x: 0, y: 0, lastPulse: performance.now(), period: 8200 };
    const positionHeart = () => {
      heart.x = window.innerWidth * 0.5;
      // Sits behind the hero; tracks a little with scroll but never leaves screen.
      heart.y = window.innerHeight * 0.42;
    };
    positionHeart();
    window.addEventListener("resize", positionHeart);

    const t0 = performance.now();

    // Precompute mandala geometry (flower-of-life-inspired). Drawn as an
    // overlay during signature; particles simultaneously reorganize toward it.
    const mandala = (() => {
      const pts: { dx: number; dy: number }[] = [];
      const ringCount = 6;
      for (let ring = 1; ring <= ringCount; ring++) {
        const count = ring * 6;
        const rad = ring * 42;
        for (let i = 0; i < count; i++) {
          const a = (i / count) * Math.PI * 2 + ring * 0.13;
          pts.push({ dx: Math.cos(a) * rad, dy: Math.sin(a) * rad });
        }
      }
      return pts;
    })();

    const drawFrame = (now: number) => {
      const t = (now - t0) / 1000;
      const W = window.innerWidth;
      const H = window.innerHeight;

      // Idle calm — after 18s of no activity, slowly damp motion.
      const idleFor = now - lastActivity;
      const idleT = Math.max(0, Math.min(1, (idleFor - 18000) / 6000)); // 0..1 over 6s
      let calm = idleT; // 0 = normal, 1 = deeply calm

      // Surge envelope.
      if (surge > 0) {
        const elapsed = now - surgeStart;
        if (elapsed < SURGE_RISE) {
          const k = elapsed / SURGE_RISE;
          surge = k * k * (3 - 2 * k);
        } else if (elapsed < SURGE_RISE + SURGE_HOLD) {
          surge = 1;
        } else {
          const k = Math.min(1, (elapsed - SURGE_RISE - SURGE_HOLD) / SURGE_FALL);
          surge = 1 - Math.pow(k, 3);
          if (k >= 1) surge = 0;
        }
      }

      // Signature envelope (single arc, 0 → 1 → 0 over SIG_LIFE).
      if (signature > 0) {
        const k = Math.min(1, (now - signatureStart) / SIG_LIFE);
        // rise fast, hold, ease-out
        signature = k < 0.15
          ? (k / 0.15)
          : k < 0.45
            ? 1
            : Math.pow(1 - (k - 0.45) / 0.55, 2);
        if (k >= 1) signature = 0;
      }

      // Conscious-breath envelope — 0 → 1 → 0 over BREATH_E_LIFE. Folded
      // into `calm` so existing damping/flow/heart paths respond.
      if (breathE > 0) {
        const k = Math.min(1, (now - breathEStart) / BREATH_E_LIFE);
        breathE = k < 0.35 ? k / 0.35 : Math.pow(1 - (k - 0.35) / 0.65, 2);
        if (k >= 1) breathE = 0;
        calm = Math.min(1, calm + breathE * 0.7);
      }

      // Breath cycle ~10s, intensified by surge + signature; calmed when idle.
      const breath = 0.5 + 0.5 * Math.sin((t / (10 + calm * 4)) * Math.PI * 2);
      const breathScale = 1 + breath * 0.012 + surge * 0.09 + signature * 0.06;
      const breathAlpha =
        (0.85 + breath * 0.15 + surge * 0.35 + signature * 0.3 + breathE * 0.25) * (1 - calm * 0.25);

      // Ease scroll shift.
      scrollShift += (scrollTarget - scrollShift) * 0.06;

      // Trail-fade — subtle glow retention during peaks.
      ctx.globalCompositeOperation = "source-over";
      const fade = 0.18 - surge * 0.09 - signature * 0.07 + calm * 0.04;
      ctx.fillStyle = `rgba(6, 8, 14, ${Math.max(0.06, fade)})`;
      ctx.fillRect(0, 0, W, H);

      // ── HEART OF THE FIELD ─────────────────────────────────────────
      // Slow autonomous pulse — brighter as coherence rises.
      if (now - heart.lastPulse > heart.period) {
        spawnRipple(heart.x, heart.y, 0.45 + coherence * 0.55, 4200, [255, 236, 176]);
        heart.lastPulse = now;
        heart.period = 6800 + Math.random() * 3400; // 6.8–10.2s
      }
      {
        const pulseK = Math.min(1, (now - heart.lastPulse) / 1800);
        const beat = 1 - pulseK; // 1 just after pulse → 0
        const baseR = 90 + coherence * 60;
        const glow = ctx.createRadialGradient(heart.x, heart.y, 0, heart.x, heart.y, baseR * (1 + beat * 0.6));
        const heartAlpha =
          (0.10 + coherence * 0.22 + beat * 0.18) * (1 - calm * 0.15);
        glow.addColorStop(0, `rgba(255, 240, 190, ${heartAlpha})`);
        glow.addColorStop(0.4, `rgba(244, 214, 117, ${heartAlpha * 0.45})`);
        glow.addColorStop(1, "rgba(244, 214, 117, 0)");
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(heart.x, heart.y, Math.max(0.1, baseR * (1 + beat * 0.6)), 0, Math.PI * 2);
        ctx.fill();
      }

      // Additive particles.
      ctx.globalCompositeOperation = "lighter";

      for (const p of particles) {
        // Drift (curl-ish). Signature reorganizes particles toward mandala pts.
        const flow = (1 + surge * 1.6 + signature * 0.8) * (1 - calm * 0.55);
        const nx = Math.sin(p.y * 0.0025 + t * 0.15 * flow + p.phase);
        const ny = Math.cos(p.x * 0.0025 + t * 0.13 * flow + p.phase);
        p.vx += nx * 0.004 * (0.5 + p.depth) * flow;
        p.vy += ny * 0.004 * (0.5 + p.depth) * flow;

        // Equilibrium spring — softens during surge/signature.
        const spring = 0.0008 * (1 - surge * 0.4 - signature * 0.5);
        p.vx += (p.hx - p.x) * spring;
        p.vy += (p.hy - p.y) * spring;

        // Signature attraction: each particle seeks a nearby mandala point.
        if (signature > 0.05) {
          const idx = (Math.floor(p.hue * 999)) % mandala.length;
          const tx = signatureX + mandala[idx].dx;
          const ty = signatureY + mandala[idx].dy;
          const k = signature * 0.008;
          p.vx += (tx - p.x) * k;
          p.vy += (ty - p.y) * k;
        }

        // Cursor curiosity.
        if (pointer.active) {
          const dx = pointer.x - p.x;
          const dy = pointer.y - p.y;
          const d2 = dx * dx + dy * dy;
          const R = 180;
          if (d2 < R * R) {
            const d = Math.sqrt(d2) + 0.001;
            const f = (1 - d / R) * 0.35 * (0.4 + p.depth) * (1 - calm * 0.6);
            p.vx += (dx / d) * f;
            p.vy += (dy / d) * f;
          }
        }

        // Ripple push.
        for (const r of ripples) {
          const age = (now - r.t) / r.life;
          if (age >= 1) continue;
          const radius = age * Math.max(W, H) * 0.55;
          const dx = p.x - r.x;
          const dy = p.y - r.y;
          const d = Math.sqrt(dx * dx + dy * dy) + 0.001;
          const band = Math.abs(d - radius);
          if (band < 40) {
            const f = ((1 - band / 40) * (1 - age) * 0.25 * r.strength) / (0.4 + p.depth);
            p.vx += (dx / d) * f;
            p.vy += (dy / d) * f;
          }
        }

        // Damping — stronger when idle for meditative calm.
        const damp = 0.94 - calm * 0.03;
        p.vx *= damp;
        p.vy *= damp;

        p.x += p.vx;
        p.y += p.vy;

        // Parallax from scroll.
        const parY = -scrollShift * 0.05 * (0.2 + p.depth);
        if (p.x < -20) p.x = W + 20;
        if (p.x > W + 20) p.x = -20;
        if (p.y < -20) p.y = H + 20;
        if (p.y > H + 20) p.y = -20;

        // Twinkle.
        p.twinkle += 0.01 + p.depth * 0.02;
        const tw = 0.65 + 0.35 * Math.sin(p.twinkle);

        // Local attention brightening.
        let attnBoost = 0;
        for (const a of attentions) {
          const ageA = (now - a.t) / a.life;
          if (ageA >= 1) continue;
          const dx = p.x - a.x;
          const dy = p.y - a.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 260) attnBoost += (1 - d / 260) * (1 - ageA) * 0.4;
        }

        // Color.
        const hueRoll = p.hue;
        const color =
          hueRoll < 0.55
            ? [244, 214, 117]
            : hueRoll < 0.85
              ? [214, 168, 62]
              : [255, 246, 220];

        // Coherence gently brightens deeper particles; signature adds gold.
        const coherenceBoost = 1 + coherence * 0.35 + signature * 0.4 + attnBoost;
        const alpha = (0.10 + p.depth * 0.55) * tw * breathAlpha * coherenceBoost;
        const rr = p.r * breathScale * (0.6 + p.depth * 0.9);

        const rOuter = Math.max(0.1, rr * 6);
        const rInner = Math.max(0.05, rr * 0.7);
        const grd = ctx.createRadialGradient(p.x, p.y + parY, 0, p.x, p.y + parY, rOuter);
        grd.addColorStop(0, `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`);
        grd.addColorStop(0.4, `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha * 0.35})`);
        grd.addColorStop(1, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0)`);
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(p.x, p.y + parY, rOuter, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${Math.min(1, alpha * 2)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y + parY, rInner, 0, Math.PI * 2);
        ctx.fill();
      }

      // Ripple rings.
      for (let i = ripples.length - 1; i >= 0; i--) {
        const r = ripples[i];
        const age = (now - r.t) / r.life;
        if (age >= 1) { ripples.splice(i, 1); continue; }
        const radius = age * Math.max(W, H) * 0.55;
        const alpha = (1 - age) * 0.22 * r.strength;
        const [rC, gC, bC] = r.color ?? [244, 214, 117];
        ctx.strokeStyle = `rgba(${rC}, ${gC}, ${bC}, ${alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(r.x, r.y, Math.max(0.1, radius), 0, Math.PI * 2);
        ctx.stroke();
      }

      // Sacred geometry overlay during signature — a flower-of-life bloom,
      // drawn very softly so it feels discovered, not displayed.
      if (signature > 0.02) {
        ctx.globalCompositeOperation = "lighter";
        const sigAlpha = signature * 0.22;
        ctx.strokeStyle = `rgba(255, 232, 168, ${sigAlpha})`;
        ctx.lineWidth = 0.75;
        const rings = 6;
        for (let ring = 1; ring <= rings; ring++) {
          const count = ring * 6;
          const rad = ring * 42 * (0.9 + signature * 0.15);
          for (let i = 0; i < count; i++) {
            const a = (i / count) * Math.PI * 2 + ring * 0.13;
            const cx = signatureX + Math.cos(a) * rad;
            const cy = signatureY + Math.sin(a) * rad;
            ctx.beginPath();
            ctx.arc(cx, cy, Math.max(0.1, 32 * (0.7 + signature * 0.3)), 0, Math.PI * 2);
            ctx.stroke();
          }
        }
      }

      ctx.globalCompositeOperation = "source-over";
    };

    let raf = 0;
    const loop = (now: number) => {
      drawFrame(now);
      raf = requestAnimationFrame(loop);
    };

    if (reducedMotion) {
      drawFrame(performance.now());
    } else {
      raf = requestAnimationFrame(loop);
    }

    const onVis = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else if (!reducedMotion) {
        raf = requestAnimationFrame(loop);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onClick);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", armPauseRipple);
      window.removeEventListener("kaf:surge", onSurge);
      window.removeEventListener("kaf:ankh-signature", onSignature);
      window.removeEventListener("kaf:attention", onAttention);
      window.removeEventListener("kaf:breath", onBreath);
      window.removeEventListener("resize", positionHeart);
      document.removeEventListener("visibilitychange", onVis);
      if (pauseTimer) window.clearTimeout(pauseTimer);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0"
      style={{
        background:
          "radial-gradient(ellipse at 50% 20%, rgba(20, 24, 44, 0.55), transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(30, 22, 10, 0.5), transparent 55%)",
      }}
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
}
